import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { inferEvidenceColumns, formatEvidenceCellValue } from '../../utils/optimizationTransformers';

/**
 * Modal that shows one or more tables of evidence (entity + metrics).
 * Data comes from suggestion.evidence (table-like keys). Columns are inferred from entries.
 * Dark glass-morphism style to match OptimizationDetailModal.
 */
const EvidenceTableModal = ({ isOpen, onClose, tables = [] }) => {
  if (!isOpen) return null;

  const hasContent = Array.isArray(tables) && tables.length > 0 && tables.some((t) => (t.entries?.length ?? 0) > 0);

  // z-index must be above OptimizationDetailModal (z-[10100]) so this modal appears on top when opened from it
  return (
    <div
      className="fixed inset-0 modal-safe-area z-[11000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="evidence-modal-title"
    >
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 border border-white/20 rounded-2xl shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/20">
          <h3 id="evidence-modal-title" className="text-sm font-semibold text-white">
            Evidencia
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-x-auto overflow-y-auto flex-1 p-4 space-y-6">
          {!hasContent ? (
            <p className="text-sm text-white/60">No hay datos de evidencia.</p>
          ) : (
            tables.map(({ title, entries, currencyCode }, tableIdx) => (
              <EvidenceTableSection key={tableIdx} title={title} entries={entries ?? []} currencyCode={currencyCode ?? null} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const EvidenceTableSection = ({ title, entries, currencyCode = null }) => {
  const columns = useMemo(() => inferEvidenceColumns(entries), [entries]);

  if (entries.length === 0) return null;

  return (
    <section>
      <h4 className="text-xs font-semibold text-white/80 uppercase tracking-wider mb-3">{title}</h4>
      <table className="w-full" aria-label={title}>
        <thead>
          <tr className="border-b border-white/30">
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-xs font-bold text-white uppercase tracking-wider ${col.key === 'name' ? 'text-left' : 'text-center'}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/20">
          {entries.map((row, i) => (
            <tr key={i} className="hover:bg-white/5 transition-colors">
              {columns.map((col) => {
                const value = col.key === 'name' ? row.name : row.metrics?.[col.key];
                const display = col.key === 'name'
                  ? (value || '-')
                  : formatEvidenceCellValue(col.format, value, currencyCode);
                return (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-sm text-white/90 ${col.key === 'name' ? 'text-left' : 'text-center'}`}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export default EvidenceTableModal;
